/**
 * RecommendedTableCard Component
 *
 * Displays a single recommended table from the AI Digital Twin analysis.
 * Supports different priority levels with color-coded styling:
 * - critical (Must-Have): Red accent
 * - high (Should-Have): Amber accent
 * - medium (Recommended): Blue accent
 * - low (Nice to Have): Gray accent
 */

import React from 'react';
import { Check, Table2, MessageSquare, Database, Star, TrendingUp, Minus } from 'lucide-react';
import { Badge } from '../ui/Badge';
import type { RecommendedTable, TablePriority } from '../../types/knowledgeGraphDesign';

interface RecommendedTableCardProps {
  table: RecommendedTable;
  isSelected: boolean;
  onToggle: () => void;
  variant?: TablePriority;
  showQuestions?: boolean;
  compact?: boolean;
}

// Priority styling configuration
const priorityStyles: Record<TablePriority, {
  border: string;
  bg: string;
  badge: string;
  badgeText: string;
  icon: React.ReactNode;
  label: string;
}> = {
  critical: {
    border: 'border-red-300',
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-800',
    badgeText: 'Must-Have',
    icon: <Star className="w-3 h-3" />,
    label: 'Critical',
  },
  high: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-800',
    badgeText: 'Should-Have',
    icon: <TrendingUp className="w-3 h-3" />,
    label: 'High Priority',
  },
  medium: {
    border: 'border-blue-300',
    bg: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-800',
    badgeText: 'Recommended',
    icon: <Database className="w-3 h-3" />,
    label: 'Medium Priority',
  },
  low: {
    border: 'border-neutral-300',
    bg: 'bg-neutral-50',
    badge: 'bg-neutral-100 text-neutral-700',
    badgeText: 'Nice to Have',
    icon: <Minus className="w-3 h-3" />,
    label: 'Low Priority',
  },
};

export const RecommendedTableCard: React.FC<RecommendedTableCardProps> = ({
  table,
  isSelected,
  onToggle,
  variant,
  showQuestions = true,
  compact = false,
}) => {
  // Use provided variant or derive from table.priority
  const priority = variant || table.priority;
  const styles = priorityStyles[priority];

  return (
    <div
      className={`
        p-3 rounded-lg border-2 cursor-pointer transition-all
        ${styles.border} ${styles.bg}
        ${isSelected ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'}
      `}
      onClick={onToggle}
    >
      <div className="flex items-start justify-between gap-3">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header with checkbox, table name, and badges */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Selection checkbox */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                isSelected
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : 'border-neutral-300 hover:border-primary-400 bg-white'
              }`}
            >
              {isSelected && <Check className="w-3 h-3" />}
            </button>

            <Table2 className="w-4 h-4 text-primary-600 flex-shrink-0" />
            <span className="font-semibold text-sm truncate">{table.table_name}</span>

            {/* Priority badge */}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles.badge}`}>
              {styles.icon}
              {styles.badgeText}
            </span>
          </div>

          {/* System and module info */}
          <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
            <Database className="w-3 h-3" />
            {table.system_name} &bull; {table.module_name}
          </p>

          {/* Reason */}
          {!compact && table.reason && (
            <p className="text-sm text-neutral-700 mt-2 line-clamp-2">
              {table.reason}
            </p>
          )}

          {/* Questions addressed */}
          {showQuestions && !compact && table.question_texts && table.question_texts.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              <MessageSquare className="w-3 h-3 text-neutral-400 mt-0.5" />
              {table.question_texts.slice(0, 2).map((q, i) => (
                <Badge key={i} variant="secondary" className="text-xs">
                  Q: {q.length > 40 ? `${q.substring(0, 40)}...` : q}
                </Badge>
              ))}
              {table.question_texts.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{table.question_texts.length - 2} more
                </Badge>
              )}
            </div>
          )}

          {/* Domains and KG areas (compact display) */}
          {!compact && (table.domains_covered?.length > 0 || table.kg_areas?.length > 0) && (
            <div className="mt-2 flex flex-wrap gap-1">
              {table.domains_covered?.slice(0, 2).map((domain, i) => (
                <Badge key={`domain-${i}`} variant="outline" className="text-xs">
                  {domain}
                </Badge>
              ))}
              {table.kg_areas?.slice(0, 2).map((area, i) => (
                <Badge key={`area-${i}`} variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                  {formatKGArea(area)}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {/* Right side - Stats */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {/* Column count */}
          {table.column_count !== undefined && (
            <Badge variant="outline" className="text-xs">
              {table.column_count} cols
            </Badge>
          )}

          {/* Relevance score */}
          {table.relevance_score !== undefined && (
            <span className="text-xs font-medium text-primary-600">
              {Math.round(table.relevance_score * 100)}% match
            </span>
          )}

          {/* Digital twin role */}
          {!compact && table.digital_twin_role && (
            <span className="text-xs text-neutral-500 text-right max-w-[120px] truncate">
              {table.digital_twin_role}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to format KG area names
function formatKGArea(area: string): string {
  return area
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export default RecommendedTableCard;
